import { Button } from "@/components/ui/button";

interface HeroSectionProps {
  onRequestAudit: () => void;
}

const HeroSection = ({ onRequestAudit }: HeroSectionProps) => {
  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-20 mb-[75px]">
      {/* Background with curved red lines - subtle on mobile, full on desktop */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30 md:opacity-80">
        {/* Left side curved lines - extended to reach edge */}
        <svg
          className="absolute bottom-0 left-0 w-[800px] h-[900px] opacity-80 -translate-x-[100px]"
          viewBox="0 0 800 900"
          fill="none"
        >
          <path d="M-200 900 Q 200 600, 150 350 T 250 -50" stroke="url(#redGradient1)" strokeWidth="2" fill="none" />
          <path
            d="M-180 900 Q 220 580, 170 330 T 270 -70"
            stroke="url(#redGradient1)"
            strokeWidth="1.5"
            fill="none"
            opacity="0.6"
          />
          <path d="M-50 850 L 200 400" stroke="url(#redGradient1)" strokeWidth="2" fill="none" />
          <defs>
            <linearGradient id="redGradient1" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="hsl(350, 100%, 50%)" stopOpacity="0" />
              <stop offset="15%" stopColor="hsl(350, 100%, 50%)" />
              <stop offset="85%" stopColor="hsl(0, 85%, 55%)" />
              <stop offset="100%" stopColor="hsl(0, 85%, 55%)" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        {/* Right side curved lines - extended to reach edge */}
        <svg
          className="absolute top-0 right-0 w-[900px] h-[1000px] opacity-80 translate-x-[100px]"
          viewBox="0 0 900 1000"
          fill="none"
        >
          <path d="M500 -100 Q 900 150, 850 500 T 750 950" stroke="url(#redGradient2)" strokeWidth="2" fill="none" />
          <path
            d="M520 -80 Q 920 170, 870 520 T 770 970"
            stroke="url(#redGradient2)"
            strokeWidth="1.5"
            fill="none"
            opacity="0.5"
          />
          <path
            d="M450 50 Q 650 250, 600 500 T 500 850"
            stroke="url(#redGradient2)"
            strokeWidth="1.5"
            fill="none"
            opacity="0.4"
            strokeDasharray="4 6"
          />
          <defs>
            <linearGradient id="redGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(350, 100%, 55%)" stopOpacity="0" />
              <stop offset="15%" stopColor="hsl(350, 100%, 55%)" />
              <stop offset="85%" stopColor="hsl(0, 85%, 50%)" />
              <stop offset="100%" stopColor="hsl(0, 85%, 50%)" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 text-center relative z-10">
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight mb-6">
          Afraid your DeFi app
          <br />
          will get <span className="text-gradient-red font-normal">hacked?</span>
        </h1>

        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
          One mistake can cost you everything.
          <br />
          We find critical vulnerabilities
          <br />
          <span className="text-primary font-semibold">before hackers do</span>.
        </p>

        <div className="flex flex-col-reverse sm:flex-row items-center justify-center gap-4">
          <Button
            variant="outline"
            size="lg"
            className="btn-primary-outline min-w-[200px] rounded-full px-8 py-6 text-base"
            asChild
          >
            <a href="https://github.com/bugblow/audits" target="_blank" rel="noopener noreferrer">
              Previous projects
            </a>
          </Button>
          <Button
            size="lg"
            className="btn-primary-filled rounded-full min-w-[240px] px-10 py-7 text-lg sm:min-w-[200px] sm:px-8 sm:py-6 sm:text-base glow-red"
            onClick={onRequestAudit}
          >
            Request an audit
          </Button>
        </div>

        {/* Social links - horizontal on mobile, below buttons */}
        <div className="flex md:hidden items-center justify-center gap-4 mt-8">
          <SocialLink href="https://x.com/BugBlow" icon="x" />
          <SocialLink href="https://www.linkedin.com/in/alexander-dolgavin-au/" icon="linkedin" />
          <SocialLink href="https://www.youtube.com/@alexdolgavin" icon="youtube" />
          <SocialLink href="https://t.me/alexdolgavin" icon="telegram" />
          <SocialLink href="https://github.com/BugBlow/audits" icon="github" />
        </div>
      </div>

      {/* Social links - right side, desktop only */}
      <div className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 hidden md:flex flex-col gap-4 z-20">
        <SocialLink href="https://x.com/BugBlow" icon="x" />
        <SocialLink href="https://www.linkedin.com/in/alexander-dolgavin-au/" icon="linkedin" />
        <SocialLink href="https://www.youtube.com/@alexdolgavin" icon="youtube" />
        <SocialLink href="https://t.me/alexdolgavin" icon="telegram" />
        <SocialLink href="https://github.com/BugBlow/audits" icon="github" />
      </div>
    </section>
  );
};

const socialLabels: Record<string, string> = {
  x: "X (Twitter)",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  telegram: "Telegram",
  github: "GitHub",
};

const SocialLink = ({ href, icon }: { href: string; icon: string }) => {
  const icons: Record<string, JSX.Element> = {
    x: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    linkedin: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
    youtube: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
    telegram: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
      </svg>
    ),
    github: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
      </svg>
    ),
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-10 h-10 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors duration-200"
      aria-label={socialLabels[icon] || icon}
    >
      {icons[icon]}
    </a>
  );
};

export default HeroSection;
